import { Pagination } from '@common/types/pagination';
import { QueryState } from '@common/types/queryState';
import { useCallback, useMemo } from 'react';

export const usePagination = (
    pageIndex: number,
    pageSize: number,
    setUrl: (update: Partial<QueryState>) => void,
) => {
    const setPageIndex = useCallback(
        (pageIndex: number) => setUrl({ pageIndex }),
        [setUrl],
    );

    const setPageSize = useCallback(
        (pageSize: number) => setUrl({ pageSize, pageIndex: 0 }),
        [setUrl],
    );

    const setPagination = useCallback(
        (updater: Pagination | ((old: Pagination) => Pagination)) => {
            const next =
                typeof updater === 'function'
                    ? updater({ pageIndex, pageSize })
                    : updater;

            const hasSizeChanged = next.pageSize !== pageSize;

            setUrl({
                pageSize: next.pageSize,
                pageIndex: hasSizeChanged ? 0 : next.pageIndex,
            });
        },
        [pageIndex, pageSize, setUrl],
    );

    return useMemo(
        () => ({
            onPaginationChange: setPagination,
            setPageIndex,
            setPageSize,
            pagination: { pageIndex, pageSize },
            pageIndex,
            pageSize,
        }),
        [pageIndex, pageSize, setPageIndex, setPageSize, setPagination],
    );
};

export type UsePaginationResult = ReturnType<typeof usePagination>;
